import {
  DiscoveryProduct,
  DiscoveryService,
  MenuItem,
  SearchProductPagination,
} from '@teko/discovery';
import { logger } from '@teko/log';
import { cleanObject } from '@teko/utils';
import { WebsiteMenu } from '@tempi/core-renderer';
import { ServiceContainer, buildTreeCategories } from 'helpers';
import { ShopFrontGetServerSidePropsContext } from 'libs';
import { fromRedisCache, redisGroupKey } from 'server/redis-client';
import { TenantEnv } from 'types';
import { PbService } from './service';
import {
  Content,
  ITempiConfig,
  ITempiLayout,
  LayoutConfig,
  PbMetaData,
  SSRInputs,
  Slot,
} from './types';

export interface PbServerSideProps {
  pbContent: Content;
  pbSSRData: SSRInputs;
  pbMetaData: PbMetaData;
  name?: string;
  menu?: WebsiteMenu[];
}

export const getPbPageServerSideProps = async (
  context: ShopFrontGetServerSidePropsContext,
  page = '/',
) => {
  try {
    const epicEcom480 = context.req.tenantEnv.epicEcom480;
    const tempiWebsiteDomain = context.req.tenantEnv.tempiWebsiteDomain;
    const { ssrData, metaData, content, ...config } = await getContent(
      context.req.tenantEnv,
      epicEcom480 ? tempiWebsiteDomain || '' : context.req.headers.host ?? '',
      context.req.headers.host || '',
      page,
      epicEcom480 || false,
    );
    // check if 404 page
    const isNotFoundPage =
      !!content && Object.keys(content).some((key) => key === '404');
    if (page !== '/' && isNotFoundPage) {
      return {
        notFound: true,
      };
    }
    return {
      props: {
        ...config,
        pbContent: content,
        pbSSRData: ssrData || {},
        pbMetaData: metaData,
      },
    };
  } catch (e) {
    logger.error(e);

    return page !== '/'
      ? {
          /** define 404 for landing page */
          notFound: true,
        }
      : { props: {} };
  }
};

export const getPbContentServerSideProps = async (
  context: ShopFrontGetServerSidePropsContext,
  page = '/',
) => {
  try {
    const epicEcom480 = context.req.tenantEnv.epicEcom480;
    const showPbPagesFromWebsiteEditor =
      context.req.tenantEnv.showPbPagesFromWebsiteEditor;
    const tempiWebsiteDomain = context.req.tenantEnv.tempiWebsiteDomain;
    const { ssrData, metaData, content, ...config } = await getContent(
      context.req.tenantEnv,
      epicEcom480 || showPbPagesFromWebsiteEditor
        ? tempiWebsiteDomain || ''
        : context.req.headers.host ?? '',
      context.req.headers.host || '',
      page,
      epicEcom480 || showPbPagesFromWebsiteEditor || false,
    );
    return {
      props: {
        ...config,
        pbContent: content,
        pbSSRData: ssrData || {},
        pbMetaData: metaData,
      },
    };
  } catch (e) {
    logger.error(e);

    return page !== '/'
      ? {
          /** define 404 for landing page */
          notFound: true,
        }
      : { props: {} };
  }
};

const getContent = async (
  tenantEnv: TenantEnv,
  host: string,
  appliedDomain: string,
  page = '/',
  isEpicEcom480: boolean,
): Promise<ITempiConfig> => {
  const { isMobile, currentLanguage } = tenantEnv || {};

  const device = isMobile ? 'mobile' : 'desktop';

  /** get content */
  const pbBuilder = new PbService(
    host,
    appliedDomain,
    isEpicEcom480,
    currentLanguage,
  );

  const {
    content,
    name = '',
    extraInfo,
    canonical,
    menu = [],
    layoutConfig = {},
  } = await pbBuilder.getContentPage(page, device);

  /**get server-side by slot & slug|menu_zone */
  const ssrInputs = await pbBuilder.getSSRInputs(
    { ...content, ...layoutConfig?.header, ...layoutConfig?.footer },
    device,
  );
  const ssrData = await getSSRData(ssrInputs, tenantEnv);

  return {
    content,
    ssrData: ssrData || {},
    metaData: cleanObject({ ...extraInfo, canonical }) || null,
    name,
    menu,
  };
};

export const getPbLayout = async (
  isMobile: boolean,
  host: string,
  appliedDomain: string,
  isEpicEcom480: boolean,
  languageCode?: string,
): Promise<ITempiLayout> => {
  const device = isMobile ? 'mobile' : 'desktop';

  /** get content */
  const pbBuilder = new PbService(
    host,
    appliedDomain,
    isEpicEcom480,
    languageCode,
  );

  const { layoutConfig, menu, extraInfo } = await pbBuilder.getLayoutPage(
    device,
  );
  return { layoutConfig: layoutConfig as LayoutConfig, menu, extraInfo };
};

export const getSSRData = async (
  ssrInputs: SSRInputs,
  tenantEnv: TenantEnv,
) => {
  const { location, currentLanguage, terminalId } = tenantEnv;
  const { discoveryService } = new ServiceContainer(tenantEnv);

  const fetchData = async (
    slot: Slot,
    params: { collectionSlug: string; menuZone?: string },
  ) => {
    let data = {};
    switch (slot) {
      case 'mega_menu':
        data = await getMenuItems(
          discoveryService,
          params.menuZone ?? '',
          terminalId,
          currentLanguage ?? '',
        );
        break;
      default:
        data = await getProductsBySlug(
          tenantEnv,
          params.collectionSlug,
          location ?? '',
        );
        break;
    }
    return { slot, serverData: data };
  };
  const outputs = await Promise.all(
    Object.entries(ssrInputs).map(async ([id, ssrInput]) => {
      const data = await Promise.all(
        ssrInput.map(async (input) => {
          const { slot, collectionSlug, menuZone } = input as never;
          return fetchData(slot, { collectionSlug, menuZone });
        }),
      );
      return { [id]: data };
    }),
  );
  // convert array => object :value
  const ssrOutputs = outputs.reduce(
    (output, item) => ({ ...output, ...item }),
    {},
  );
  return ssrOutputs;
};

export const getMenuItems = async (
  discoveryService: DiscoveryService,
  menuZone: string,
  terminalId: number,
  currentLanguage: string,
) => {
  try {
    const menuItems = await fromRedisCache(
      redisGroupKey.MENU_ITEMS,
      `${terminalId}_${menuZone}_${currentLanguage}`,
      async () => {
        logger.info('get menuItems from discovery');
        const response = await discoveryService.getMenuItems(menuZone);

        return buildTreeCategories(response?.result?.menuItems || []);
      },
      7200000,
    );
    return { menuItems };
  } catch (e) {
    logger.error(e);
    // error object could be anything
    // @ts-ignore
    return { menuItems: [] as MenuItem[], error: e.response };
  }
};

export const getProductsBySlug = async (
  tenantEnv: TenantEnv,
  slug: string,
  location: string,
  limit = 20,
  page = 1,
) => {
  try {
    const { discoveryServiceV2, discoveryHelper } = new ServiceContainer(
      tenantEnv,
    );

    // getting featureFlags

    const response = await discoveryServiceV2.search({
      slug,
      pageSize: limit,
      page,
    });

    return cleanObject({
      products:
        response?.products?.map((product) =>
          discoveryHelper.cleanupDiscoveryV2SearchProduct(product),
        ) || [],
      pagination: {
        totalItems: response?.total || 0,
        totalPages: Math.ceil(
          (response?.total || 0) / (response.pageSize || limit),
        ),
      },
    });
  } catch (e) {
    logger.error(e);
    return {
      // response from DiscoveryService can be anything
      // @ts-ignore
      error: e.response || {},
      products: [] as DiscoveryProduct[],
      pagination: {
        totalItems: 0,
        totalPages: 1,
      } as SearchProductPagination,
    };
  }
};
